/* eslint-disable no-mixed-operators */
import mapStyles from 'components/GoogleMap/mapStyle.json';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_STATIC_MAP_API_KEY,
  GOOGLE_MAPS_STATIC_MAP_MARKER_URL,
} from '../../config';

const earthRadius = 3958.75; // in miles

export const rad2deg = (rad) => (rad * 180) / Math.PI;
export const deg2rad = (deg) => (deg * Math.PI) / 180;

// Haversine formula of distance calculation
export function distance(latA, lngA, latB, lngB) {
  const dLat = deg2rad(latB - latA);
  const dLng = deg2rad(lngB - lngA);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) *
      Math.sin(dLng / 2) *
      Math.cos(deg2rad(latA)) *
      Math.cos(deg2rad(latB));

  const c = 2 * Math.asin(Math.sqrt(a));
  return earthRadius * c;
}

export function geoDistance(ptA, ptB) {
  if (!ptA || !ptB) return 0;
  return Math.abs(distance(ptA.lat, ptA.lng, ptB.lat, ptB.lng));
}

export function googleMapStylesToQueryParams(style) {
  return style
    .map(
      (def) =>
        `feature:${def.featureType}|` +
        (def.elementType ? `element:${def.elementType}|` : '') +
        def.stylers
          .map((attrs) =>
            Object.keys(attrs)
              .map((key) => `${key}:${attrs[key]}`)
              .join('|')
          )
          .join('|')
    )
    .map((param) => `style=${param}`);
}

export function staticMap({
  markerLatLngs,
  width = 350,
  height = 350,
  withStyle = true,
  staticMarkerUrl = GOOGLE_MAPS_STATIC_MAP_MARKER_URL,
}) {
  const markerParams = markerLatLngs.map(
    (latLng) =>
      `markers=${encodeURIComponent(
        `icon:${staticMarkerUrl}|${latLng.lat || latLng[0]},${
          latLng.lng || latLng[1]
        }`
      )}`
  );
  const styleParams = withStyle ? googleMapStylesToQueryParams(mapStyles) : [];
  const otherParams = [
    `size=${width}x${height}`,
    `key=${GOOGLE_MAPS_STATIC_MAP_API_KEY}`,
  ];

  const params = otherParams.concat(markerParams).concat(styleParams);

  return `https://maps.googleapis.com/maps/api/staticmap?${params.join('&')}`;
}

const NUM_CIRCLE_SIDES = 16;

export function googleMapsOverlayToGeometry(google, mapsEvent) {
  const { overlay } = mapsEvent;
  switch (mapsEvent.type) {
    case google.maps.drawing.OverlayType.MARKER:
      return new google.maps.Data.Point(overlay.getPosition());
    case google.maps.drawing.OverlayType.RECTANGLE: {
      const bounds = overlay.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const poly = [
        sw,
        { lat: sw.lat(), lng: ne.lng() },
        ne,
        { lat: ne.lat(), lng: sw.lng() },
      ];
      return new google.maps.Data.Polygon([poly]);
    }
    case google.maps.drawing.OverlayType.POLYGON:
      return new google.maps.Data.Polygon([overlay.getPath().getArray()]);
    case google.maps.drawing.OverlayType.POLYLINE:
      return new google.maps.Data.LineString(overlay.getPath().getArray());
    case google.maps.drawing.OverlayType.CIRCLE: {
      const center = overlay.getCenter();
      const radius = overlay.getRadius();
      const points = [];
      for (let i = 0; i < NUM_CIRCLE_SIDES; i++) {
        points.push(
          google.maps.geometry.spherical.computeOffset(
            center,
            radius,
            i * (360 / NUM_CIRCLE_SIDES)
          )
        );
      }
      points.push(points[0]);
      return new google.maps.Data.Polygon([points]);
    }
    default:
      return null;
  }
}

export function googleMapsOverlayToGeoJSON(google, mapsEvent, callback) {
  const { overlay } = mapsEvent;
  const geometry = googleMapsOverlayToGeometry(google, mapsEvent);
  const feature = new google.maps.Data.Feature({ geometry });
  overlay.map.data.add(feature);
  overlay.map.data.toGeoJson((result) => {
    overlay.map.data.remove(feature);
    overlay.setMap(null);
    callback(result);
  });
}

export function getLatLng(input) {
  const isLatLngLiteral =
    input.hasOwnProperty('lat') && input.hasOwnProperty('lng');
  if (isLatLngLiteral) return { lat: input.lat, lng: input.lng };

  const isLatLngArray =
    Array.isArray(input) &&
    input.length === 2 &&
    typeof input[0] === 'number' &&
    typeof input[1] === 'number';
  if (isLatLngArray) return { lat: input[1], lng: input[0] };

  return null;
}

export async function googleGeocode(str) {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?key=${GOOGLE_MAPS_API_KEY}&address=${encodeURIComponent(
    str
  )}`;
  const geocoderResponse = await fetch(url).then((res) => res.json());
  return geocoderResponse.results[0].geometry.location;
}

export function googleMapTourLink(buildings) {
  const baseUrl = 'https://www.google.com/maps/dir';
  const urlArgs = buildings.reduce(
    (url, building) =>
      `${url}/${building.address},${building.city},${building.state}`,
    ''
  );
  return (baseUrl + urlArgs).replace(/\s/gi, '+');
}
